/**
 * @fileoverview Input/output schemas for the character-extractor-chunked-v2 agent.
 *
 * This agent is designed for processing large documents in chunks. It takes:
 * - A chunk of text to analyze
 * - Previously extracted characters (partialCharacters)
 * - Chunk metadata for context
 *
 * It outputs a merged list of characters, combining new finds with existing ones.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { CharacterV2IdeaSchema } from '@/domains/characters/schemas';
import { ChunkInfoSchema } from '@/lib/content-chunking';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  /**
   * The current chunk of text to analyze.
   * May include overlap from adjacent chunks for context continuity.
   */
  textChunk: z.string().describe('The current chunk of text to analyze for characters.'),

  /**
   * Characters extracted from previous chunks.
   * Empty array for the first chunk.
   * The agent should merge new findings with this list.
   */
  partialCharacters: z.array(CharacterV2IdeaSchema).describe(
    'Characters extracted from previous chunks. Empty for first chunk. Agent should merge new findings with this list.'
  ),

  /**
   * Metadata about this chunk's position in the document.
   * Helps the agent understand context (first chunk, middle, last).
   */
  chunkInfo: ChunkInfoSchema.describe('Metadata about this chunk position in the full document.'),

  /**
   * Optional story context (e.g., from DNA extraction) to help inform character analysis.
   */
  storyContext: z.string().optional().describe('Optional story context (genre, setting, etc.) to inform character analysis.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Summary of what changed in this chunk (useful for debugging and UI feedback).
 */
export const ChunkProcessingSummarySchema = z.object({
  /** Number of new characters discovered in this chunk */
  newCount: z.number().min(0).describe('Number of new characters discovered in this chunk (integer >= 0).'),

  /** Number of existing characters that were updated with new information */
  amendedCount: z.number().min(0).describe('Number of existing characters amended with new information (integer >= 0).'),

  /** Number of characters that remained unchanged */
  unchangedCount: z.number().min(0).describe('Number of characters that remained unchanged (integer >= 0).'),
}).describe('Summary of character extraction results for this chunk.');

export type ChunkProcessingSummary = z.infer<typeof ChunkProcessingSummarySchema>;

export const OutputSchema = z.object({
  /**
   * Complete merged list of all characters.
   * Includes both previously extracted characters (potentially amended)
   * and newly discovered characters from this chunk.
   */
  characters: z.array(CharacterV2IdeaSchema).describe(
    'Complete merged list of all characters including previous and newly discovered.'
  ),

  /**
   * Optional summary of what changed in this chunk.
   * Useful for progress tracking and debugging.
   */
  summary: ChunkProcessingSummarySchema.optional().describe(
    'Optional summary of extraction results for this chunk.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
